import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import {
  X,
  ImagePlus,
  Star,
  Trash,
  Trash2,
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  Layers,
  Plus,
  Pencil,
  Info,
} from "lucide-react";
import VariantImageManager from "./VariantImageManager";
import { isHeicFile, convertHeicToJpeg } from "../utils/heicConvert";
import { useBulkUpload } from "../hooks/useBulkUpload";
import { AgregarVariosModal } from "./AgregarVariosModal";

export interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
  tempFile?: File; // For temporary files before upload
  fileName?: string; // Original filename for saving
}

export interface Variant {
  id: string;
  nombre: string;
  image_url?: string;
  images?: ProductImage[];
  created_at?: string; // Database timestamp for ordering variants
}

interface VariantFormProps {
  editingVariant: Variant | null;
  newProduct: { nombre: string; images: ProductImage[] };
  loading: boolean;
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  onCancel: () => void;
  onSave: () => void; // This will be our unified save function for both adding and updating
  onImageUpload: (variantId: string, file: File) => void;
  onDeleteImage: (variantId: string, imageId: number) => void;
  onSetPrincipalImage: (variantId: string, imageId: number) => void;
  onReorderImages: (
    variantId: string,
    imageId: number,
    direction: "up" | "down",
  ) => void;
  setEditingVariant: React.Dispatch<React.SetStateAction<Variant | null>>;
  setNewProduct: React.Dispatch<
    React.SetStateAction<{ nombre: string; images: ProductImage[] }>
  >;
  onDeleteVariant?: (variantId: string) => void;
  rubro?: string;
  // Bulk upload (only relevant when !editingVariant)
  idCompany?: string | null;
  userPlan?: string;
  onBulkSuccess?: () => Promise<void> | void;
  brandId?: string | null;
}

// Temporary Image Manager component for handling images before uploading
export const TempImageManager = ({
  images,
  imageError,
  onChange,
  rubro,
  onBulkUploadClick,
  importSlot,
}: {
  images: ProductImage[];
  imageError: string | null;
  onChange: (images: ProductImage[]) => void;
  rubro?: string;
  onBulkUploadClick?: () => void;
  importSlot?: React.ReactNode;
}) => {
  // Determine which images and instructions to show based on rubro
  const isFashion = rubro === "Moda e Indumentaria";
  const isHomeDecor = rubro === "Hogar y Decoración";

  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto siendo utilizado",
      };
    } else if (isHomeDecor) {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto en contexto",
      };
    } else {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto en contexto",
      };
    }
  };

  const exampleImages = getExampleImages();
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  const [convertingHeic, setConvertingHeic] = useState(false);
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        alert(
          "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
        );
        e.target.value = "";
        return;
      }
      const newImageId = -1 * (images.length + 1); // Use negative IDs for temp images
      const isPrincipal = false; // Never set new images as principal by default

      try {
        // Check if the file is a HEIC image
        let processedFile = file;
        let processedUrl = "";

        if (isHeicFile(file)) {
          try {
            setConvertingHeic(true);
            const converted = await convertHeicToJpeg(file, 0.8);
            processedFile = converted.file;
            processedUrl = converted.url;
          } catch (err) {
            throw new Error(
              "No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg",
            );
          } finally {
            setConvertingHeic(false);
          }
        } else {
          processedUrl = URL.createObjectURL(file);
        }

        // Create new temp image entry
        const newImage: ProductImage = {
          id: newImageId,
          url: processedUrl,
          principal: isPrincipal,
          orden: images.length + 1,
          producto_variante_id: 0,
          tempFile: processedFile,
        };

        onChange([...images, newImage]);
      } catch (error) {
        setConvertingHeic(false);
        console.error("Error processing image file:", error);
        alert(
          error instanceof Error
            ? error.message
            : "Error al procesar la imagen. Por favor, intente con otra imagen.",
        );
      }

      e.target.value = ""; // Reset input
    }
  };

  const handleDelete = (imageId: number) => {
    // Check if this is the last image
    if (images.length <= 1) {
      setShowLastImageWarning(true);
      return;
    }

    // Filter out the image to delete
    const updatedImages = images.filter((img) => img.id !== imageId);

    // If we deleted the principal image and there are no principal images left, set the first one as principal
    if (
      updatedImages.length > 0 &&
      !updatedImages.some((img) => img.principal)
    ) {
      updatedImages[0].principal = true;
    }

    onChange(updatedImages);

    // Revoke object URL to avoid memory leaks
    const deletedImg = images.find((img) => img.id === imageId);
    if (deletedImg && deletedImg.id < 0) {
      // Only for temp images
      URL.revokeObjectURL(deletedImg.url);
    }
  };

  const handleSetPrincipal = (imageId: number) => {
    const updatedImages = images.map((img) => ({
      ...img,
      principal: img.id === imageId,
    }));

    onChange(updatedImages);
  };

  return (
    <div className="mt-4">
      <div className="mb-3">
        <input
          type="file"
          id="temp-image-upload"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* Guidance panel: correct vs incorrect examples */}
        {images.length === 0 && (
          <div className="mt-3 bg-surface-card border border-neutral-200 rounded-lg p-3 sm:p-4">
            <div className="flex flex-col gap-3">
              {/* Thumbnails row */}
              <div className="w-full flex items-center gap-3">
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                    <img
                      src={exampleImages.img1}
                      alt={exampleImages.alt1}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span
                      aria-label="Correcta"
                      className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                    <img
                      src={exampleImages.img2}
                      alt={exampleImages.alt2}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span
                      aria-label="Correcta"
                      className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
              {/* Instructions (inline with thumbnails on desktop, below on mobile) */}
              <ul className="text-[length:var(--text-caption)] text-neutral-600 list-disc pl-4 space-y-1">
                <li>
                  Subí una foto en donde se vea claramente el producto que
                  querés vender.
                </li>
                {isFashion && (
                  <li>
                    Fotos del producto siendo utilizado (sin mostrar el rostro)
                    funcionan mejor.
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Converting HEIC indicator */}
        {convertingHeic && (
          <div className="mt-3 flex items-center justify-center gap-3 p-4 border border-neutral-200 rounded-md bg-surface-card">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
            <p className="text-[length:var(--text-body)] text-neutral-600">
              Subiendo imagen...
            </p>
          </div>
        )}
        {/* Upload button below instructions; full width on mobile */}
        <div className="mt-3 flex flex-col gap-2">
          <label
            htmlFor="temp-image-upload"
            className={`inline-flex w-full sm:w-auto justify-center items-center h-[var(--btn-h)] px-4 shadow-sm text-[length:var(--text-body)] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card" : images.length === 0 ? "bg-brand-primary text-white hover:bg-brand-primary-hover" : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"}`}
          >
            <ImagePlus size={16} className="mr-2" />
            {convertingHeic
              ? "Subiendo..."
              : images.length === 0
                ? "Subir imagen del producto"
                : "Agregar otra imagen"}
          </label>
          {onBulkUploadClick && images.length === 0 && !convertingHeic && (
            <button
              type="button"
              onClick={onBulkUploadClick}
              className="inline-flex w-full sm:w-auto justify-center items-center h-[var(--btn-h)] px-4 border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100 rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
              title="Subí varias imágenes a la vez; cada una será un producto distinto"
            >
              <Layers size={16} className="mr-2" />
              Subir varios productos
            </button>
          )}
          {importSlot && images.length === 0 && !convertingHeic && importSlot}
        </div>
      </div>

      {imageError && (
        <div className="mb-3 text-[length:var(--text-body)] text-error">
          {imageError}
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative border border-neutral-200 rounded-lg overflow-hidden"
            >
              <div className="relative">
                <img
                  src={image.url}
                  alt="Imagen de producto"
                  className="w-full h-40 object-cover"
                />
                {image.principal && (
                  <div className="absolute top-2 left-2 bg-brand-primary text-white text-[length:var(--text-caption)] px-2 py-0.5 rounded-md font-medium">
                    Principal
                  </div>
                )}
              </div>
              <div className="p-2 bg-surface-card flex justify-between">
                <div className="flex space-x-2">
                  {!image.principal && (
                    <button
                      type="button"
                      onClick={() => handleSetPrincipal(image.id)}
                      title="Establecer como principal"
                      className="text-neutral-400 hover:text-brand-primary transition-colors"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(image.id)}
                    title="Eliminar imagen"
                    className="text-neutral-400 hover:text-error transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
          <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-md w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                No se puede eliminar
              </h3>
              <button
                onClick={() => setShowLastImageWarning(false)}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="w-10 h-10 rounded-full bg-warning-soft flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                El producto debe tener al menos una imagen. No se puede eliminar
                la última imagen.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLastImageWarning(false)}
                className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Row for a bulk-upload item: shows name as plain text with pencil edit toggle.
// Clicking pencil switches to input; blur or Enter commits and returns to text.
const BulkRow = ({
  item,
  defaultName,
  saving,
  onRename,
  onRemove,
}: {
  item: import("../hooks/useBulkUpload").BulkItem;
  defaultName: string;
  saving: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.nombre);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    onRename(draft.trim());
    setEditing(false);
  };
  const cancel = () => {
    setDraft(item.nombre);
    setEditing(false);
  };

  const rowBorder =
    item.status === "error"
      ? "border-error/40 bg-error-soft/30"
      : item.status === "success"
        ? "border-green-300 bg-green-50"
        : item.status === "uploading"
          ? "border-brand-primary/30 bg-brand-primary/5"
          : "border-neutral-200 bg-white";

  const displayName = item.nombre.trim() || defaultName;
  const readOnly = saving || item.status === "success";

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${rowBorder}`}
    >
      <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-neutral-100">
        <img
          src={item.previewUrl}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder={defaultName}
            maxLength={100}
            className="w-full px-2 py-1 text-[14px] rounded border border-brand-primary bg-white focus:ring-1 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[14px] font-medium text-neutral-900 truncate"
              title={displayName}
            >
              {displayName}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setDraft(item.nombre);
                  setEditing(true);
                }}
                className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-brand-primary hover:bg-neutral-100 transition-colors"
                aria-label="Editar nombre"
                title="Editar nombre"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {item.status === "error" && item.error && (
          <p className="text-[11px] text-error mt-0.5 truncate">{item.error}</p>
        )}
      </div>
      <div className="shrink-0 w-7 h-7 flex items-center justify-center">
        {item.status === "uploading" && (
          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        )}
        {item.status === "success" && (
          <CheckCircle2 className="w-5 h-5 text-green-600" />
        )}
        {(item.status === "pending" || item.status === "error") && !saving && (
          <button
            type="button"
            onClick={onRemove}
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-error hover:bg-error-soft transition-colors"
            aria-label="Eliminar imagen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Row para un grupo de archivos detectados por stem común. La cantidad
// de archivos y el status se derivan del grupo; el resto es similar a
// `BulkRow` (preview cuadrada + nombre editable + remove).
const BulkGroupRow = ({
  group,
  name,
  saving,
  onRename,
  onRemove,
}: {
  group: import("../utils/groupFilesByStem").FileGroup<
    import("../hooks/useBulkUpload").BulkItem
  >;
  name: string;
  saving: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // El status del grupo: todos los items deberían compartirlo, pero por
  // si quedan desincronizados al toggle-ear, tomamos el peor (error
  // domina, después uploading, después pending, después success).
  const aggregateStatus = (() => {
    const statuses = group.files.map((it) => it.status);
    if (statuses.includes("error")) return "error" as const;
    if (statuses.includes("uploading")) return "uploading" as const;
    if (statuses.every((s) => s === "success")) return "success" as const;
    return "pending" as const;
  })();
  const aggregateError = group.files.find((it) => it.error)?.error;

  const rowBorder =
    aggregateStatus === "error"
      ? "border-error/40 bg-error-soft/30"
      : aggregateStatus === "success"
        ? "border-green-300 bg-green-50"
        : aggregateStatus === "uploading"
          ? "border-brand-primary/30 bg-brand-primary/5"
          : "border-neutral-200 bg-white";

  const readOnly = saving || aggregateStatus === "success";
  const previewUrl = group.files[0]?.previewUrl;

  const commit = () => {
    onRename(draft.trim());
    setEditing(false);
  };
  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${rowBorder}`}
    >
      <div className="relative w-14 h-14 rounded-md overflow-hidden shrink-0 bg-neutral-100">
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        )}
        {group.files.length > 1 && (
          <span
            className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full bg-black/65 text-white text-[10.5px] font-semibold leading-none"
            aria-label={`${group.files.length} imágenes`}
          >
            +{group.files.length - 1}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder={group.baseName}
            maxLength={100}
            className="w-full px-2 py-1 text-[14px] rounded border border-brand-primary bg-white focus:ring-1 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[14px] font-medium text-neutral-900 truncate"
              title={name}
            >
              {name}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setDraft(name);
                  setEditing(true);
                }}
                className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-brand-primary hover:bg-neutral-100 transition-colors"
                aria-label="Editar nombre"
                title="Editar nombre"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        <p className="text-[11.5px] text-neutral-500 mt-0.5">
          {group.files.length === 1
            ? "1 imagen"
            : `${group.files.length} imágenes agrupadas`}
        </p>
        {aggregateStatus === "error" && aggregateError && (
          <p className="text-[11px] text-error mt-0.5 truncate">
            {aggregateError}
          </p>
        )}
      </div>
      <div className="shrink-0 w-7 h-7 flex items-center justify-center">
        {aggregateStatus === "uploading" && (
          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        )}
        {aggregateStatus === "success" && (
          <CheckCircle2 className="w-5 h-5 text-green-600" />
        )}
        {(aggregateStatus === "pending" || aggregateStatus === "error") &&
          !saving && (
            <button
              type="button"
              onClick={onRemove}
              className="w-7 h-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-error hover:bg-error-soft transition-colors"
              aria-label="Eliminar grupo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
      </div>
    </div>
  );
};

const VariantForm: React.FC<VariantFormProps> = ({
  editingVariant,
  newProduct,
  loading,
  uploadingImages,
  imageError,
  onCancel,
  onSave,
  onImageUpload,
  onDeleteImage,
  onSetPrincipalImage,
  onReorderImages,
  setEditingVariant,
  setNewProduct,
  onDeleteVariant,
  rubro,
  idCompany = null,
  userPlan = "gratis",
  onBulkSuccess,
  brandId = null,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const [variosOpen, setVariosOpen] = useState(false);

  // Bulk upload state (only used in add mode)
  const bulk = useBulkUpload({
    idCompany,
    userPlan,
    onSuccess: onBulkSuccess,
    brandId,
  });
  const inBulkMode = !editingVariant && bulk.items.length > 0;

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleClose = useCallback(() => {
    if (bulk.saving) return; // block close mid-bulk-upload
    setAnimated(false);
    setTimeout(() => {
      // If we're adding a new product (not editing), clear temporary state
      if (!editingVariant) {
        try {
          newProduct.images.forEach((img) => {
            if (img.id < 0 && img.url && img.url.startsWith("blob:")) {
              URL.revokeObjectURL(img.url);
            }
          });
        } catch (e) {
          // No-op
        }
        setNewProduct({ nombre: "", images: [] });
        bulk.clearAll();
      }
      onCancel();
    }, 300);
  }, [editingVariant, newProduct, setNewProduct, onCancel, bulk]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    else setDragY(0);
  };

  // Determine if save is disabled (used for styling)
  const isSaveDisabled = editingVariant
    ? !editingVariant.nombre
    : inBulkMode
      ? bulk.items.length === 0 || bulk.saving || !idCompany
      : newProduct.images.length === 0 || loading;

  const handleSaveWithValidation = async () => {
    if (editingVariant) {
      if (!editingVariant.nombre?.trim()) {
        setNameError(true);
        setShakeKey((k) => k + 1);
        return;
      }
      onSave();
      return;
    }

    if (inBulkMode) {
      if (bulk.items.length === 0) return;
      const result = await bulk.saveAll();
      if (result === "all-success") {
        setTimeout(() => handleClose(), 1000);
      }
      return;
    }

    if (newProduct.images.length === 0) return;
    onSave();
  };

  const title = editingVariant
    ? "Editar producto"
    : inBulkMode
      ? "Agregar varios productos"
      : "Agregar producto";

  // Cantidad de productos a crear: cuando el toggle "Usar nombres" está
  // activo, es la cantidad de grupos detectados (no la de archivos).
  const bulkProductCount = bulk.useFileNames
    ? bulk.groups.length
    : bulk.items.length;

  const saveButtonLabel = editingVariant
    ? loading
      ? "Guardando..."
      : "Listo"
    : inBulkMode
      ? bulk.saving
        ? "Guardando..."
        : `Crear ${bulkProductCount} ${
            bulkProductCount === 1 ? "producto" : "productos"
          }`
      : loading
        ? "Guardando..."
        : "Guardar";

  const saveButtonLabelMobile = editingVariant
    ? loading
      ? "Guardando"
      : "Listo"
    : inBulkMode
      ? bulk.saving
        ? "Guardando"
        : `Crear ${bulkProductCount}`
      : loading
        ? "Guardando"
        : "Guardar";

  const anySaving = loading || bulk.saving;

  /* ── Saving state (slides up in place of the form) ── */
  const savingState = (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none transition-all duration-300 ease-out ${
        loading
          ? "opacity-100 translate-y-0 delay-150"
          : "opacity-0 translate-y-3"
      }`}
    >
      <Loader2 className="w-7 h-7 animate-spin text-brand-primary mb-3" />
      <p className="text-[17px] font-semibold text-neutral-900">
        Guardando producto…
      </p>
      <p className="text-[15px] text-neutral-500 mt-1">
        Esto puede tardar unos segundos
      </p>
    </div>
  );

  // Whether the "Subir varios productos" entry point should be offered.
  // Only in add mode, when we have the required context (idCompany+onBulkSuccess),
  // and there are no single-product images already loaded.
  const canOfferBulk =
    !editingVariant &&
    !!idCompany &&
    !!onBulkSuccess &&
    newProduct.images.length === 0;

  const bulkContent = (
    <div className="space-y-3">
      {/* Header / limit hint */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-[length:var(--text-body)] font-medium text-neutral-700">
            Productos a crear
          </label>
          <span
            className={`text-[13px] font-medium ${
              bulk.items.length >= bulk.limit
                ? "text-error"
                : "text-neutral-500"
            }`}
          >
            {bulk.items.length}/{bulk.limit}
          </span>
        </div>
        <p className="text-[13px] text-neutral-500">
          {bulk.useFileNames
            ? "Cada archivo se sube como un producto, usando el nombre del archivo. Imágenes con nombres similares (ej. remera_1.png, remera_2.png) se agrupan automáticamente."
            : "Cada imagen se creará como un producto distinto. Podés editar el nombre ahora o después."}
          {bulk.isFreeLikePlan && (
            <span> Mejorá tu plan para subir hasta 50 por lote.</span>
          )}
        </p>
      </div>

      {/* Toggle "Usar nombres de archivo" — solo desktop. En mobile el
          espacio es ajustado y la mecánica (renombrar y agrupar) queda
          mejor con el flujo single-row de toda la vida. La aclaración
          larga vive en un tooltip que aparece al pasar el mouse por
          encima del título o del ícono (i). */}
      <div className="hidden sm:flex items-center gap-3 rounded-md border border-neutral-200 bg-white p-3">
        <div className="flex-1 min-w-0">
          <div className="group/info relative inline-flex items-center gap-1.5">
            <span
              id="bulk-use-filenames-label"
              className="text-[13.5px] font-semibold text-neutral-900 cursor-default"
            >
              Usar nombres de archivo
            </span>
            <Info
              className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Más información"
            />
            {/* Tooltip — visible al hover del wrapper (cubre título + ícono).
                Posicionado abajo a la izquierda, con flechita arriba. */}
            <div
              role="tooltip"
              className="pointer-events-none absolute left-0 top-full mt-2 z-20 w-[280px] rounded-md bg-neutral-900 text-white text-[12px] leading-snug px-3 py-2 shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover/info:opacity-100 group-hover/info:translate-y-0"
            >
              <span
                aria-hidden="true"
                className="absolute -top-1 left-3 w-2 h-2 bg-neutral-900 rotate-45"
              />
              Cada producto toma el nombre del archivo (sin extensión).
              Archivos como{" "}
              <span className="font-mono">remera_negra_1.png</span>,{" "}
              <span className="font-mono">remera_negra-2.png</span> o{" "}
              <span className="font-mono">remera_negra(3).png</span> se
              agrupan como un solo producto.
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={bulk.useFileNames}
          aria-labelledby="bulk-use-filenames-label"
          onClick={() => bulk.setUseFileNames(!bulk.useFileNames)}
          disabled={bulk.saving}
          className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary/30 disabled:opacity-40 disabled:cursor-not-allowed ${
            bulk.useFileNames ? "bg-brand-primary" : "bg-neutral-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              bulk.useFileNames ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* Global error / partial-fail banner */}
      {bulk.error && (
        <div className="flex items-start text-error-text bg-error-soft border border-error/20 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mr-2 shrink-0 mt-0.5" />
          <span className="text-[13px]">{bulk.error}</span>
        </div>
      )}

      {/* Progress bar while saving */}
      {bulk.saving && bulk.progress.total > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1 text-[13px] text-neutral-500">
            <span>
              Guardando {bulk.progress.done}/{bulk.progress.total}...
            </span>
          </div>
          <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary transition-[width] duration-300 ease-out"
              style={{
                width: `${
                  bulk.progress.total === 0
                    ? 0
                    : Math.round(
                        (bulk.progress.done / bulk.progress.total) * 100,
                      )
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Bulk items list — modo agrupado (cuando useFileNames) o el
          flujo single-row de toda la vida. */}
      <div className="space-y-2">
        {bulk.useFileNames
          ? bulk.groups.map((group) => (
              <BulkGroupRow
                key={group.key}
                group={group}
                name={bulk.groupDisplayName(group)}
                saving={bulk.saving}
                onRename={(name) => bulk.updateGroupName(group.key, name)}
                onRemove={() => bulk.removeGroup(group.key)}
              />
            ))
          : bulk.items.map((item, idx) => (
              <BulkRow
                key={item.id}
                item={item}
                defaultName={bulk.defaultNameFor(idx)}
                saving={bulk.saving}
                onRename={(name) => bulk.updateName(item.id, name)}
                onRemove={() => bulk.removeItem(item.id)}
              />
            ))}
      </div>

      {/* Add more button */}
      {bulk.items.length < bulk.limit && !bulk.saving && (
        <button
          type="button"
          onClick={bulk.openFilePicker}
          disabled={bulk.convertingHeic}
          className="w-full inline-flex items-center justify-center gap-2 h-10 border border-dashed border-neutral-300 rounded-md text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {bulk.convertingHeic ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          {bulk.convertingHeic ? "Convirtiendo..." : "Agregar más imágenes"}
        </button>
      )}
    </div>
  );

  const formContent = inBulkMode ? (
    bulkContent
  ) : (
    <div className="space-y-4">
      <div>
        <label className="block text-[length:var(--text-body)] font-medium text-neutral-700 mb-1">
          {editingVariant ? "Nombre" : "Nombre (opcional)"}
        </label>
        <input
          type="text"
          value={editingVariant ? editingVariant.nombre : newProduct.nombre}
          onChange={(e) => {
            if (editingVariant) {
              setEditingVariant({ ...editingVariant, nombre: e.target.value });
            } else {
              setNewProduct({ ...newProduct, nombre: e.target.value });
            }
          }}
          key={shakeKey}
          className={`w-full h-[var(--btn-h)] px-4 border rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors ${nameError ? "border-error animate-shake" : "border-neutral-200"}`}
          onFocus={() => nameError && setNameError(false)}
          placeholder={
            editingVariant
              ? "Nombre del producto"
              : "Déjalo vacío para que lo complete la IA"
          }
        />
      </div>

      <div>
        <label className="block text-[length:var(--text-body)] font-medium text-neutral-700 mb-1">
          Imágenes
        </label>
        {editingVariant ? (
          <VariantImageManager
            variantId={editingVariant.id || ""}
            images={editingVariant.images || []}
            uploadingImages={uploadingImages}
            imageError={imageError}
            onUpload={onImageUpload}
            onDelete={onDeleteImage}
            onSetPrincipal={onSetPrincipalImage}
            onReorder={onReorderImages}
            showHeader={false}
          />
        ) : (
          <>
            <TempImageManager
              images={newProduct.images}
              imageError={imageError}
              onChange={(newImages) =>
                setNewProduct({ ...newProduct, images: newImages })
              }
              rubro={rubro}
              onBulkUploadClick={
                canOfferBulk ? () => setVariosOpen(true) : undefined
              }
            />
            <AgregarVariosModal
              open={variosOpen}
              companyId={idCompany}
              brandId={brandId}
              userPlan={userPlan}
              onClose={() => setVariosOpen(false)}
              onSaved={async () => {
                if (onBulkSuccess) await onBulkSuccess();
              }}
            />
          </>
        )}
      </div>
    </div>
  );

  /* ── Shared action buttons ── */
  const deleteButton =
    editingVariant && onDeleteVariant ? (
      <>
        {/* Mobile: icon-only */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={loading}
          className={`sm:hidden h-[var(--btn-h)] w-[var(--btn-h)] inline-flex items-center justify-center bg-error-soft hover:bg-error text-error hover:text-white border border-error/20 rounded-md shadow-sm transition-colors ${loading ? "opacity-75 cursor-not-allowed" : ""}`}
          title="Eliminar producto"
        >
          <Trash className="w-4 h-4" />
        </button>
        {/* Desktop: full button */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={loading}
          className={`hidden sm:inline-flex h-[var(--btn-h)] px-4 items-center justify-center gap-2 text-[length:var(--text-body)] font-medium rounded-md shadow-sm transition-colors bg-error hover:bg-red-600 text-white ${loading ? "opacity-75 cursor-not-allowed" : ""}`}
        >
          Eliminar producto
        </button>
      </>
    ) : (
      <span />
    );

  /* ── Delete confirmation modal ── */
  const deleteConfirmModal =
    editingVariant && onDeleteVariant && showDeleteConfirm ? (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[70]">
        <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
              Eliminar producto
            </h3>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mb-6">
            <div className="w-10 h-10 rounded-full bg-error-soft flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-5 h-5 text-error" />
            </div>
            <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
              ¿Seguro que querés eliminar este producto? Esta acción no se puede
              deshacer.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                onDeleteVariant(editingVariant.id);
                setShowDeleteConfirm(false);
              }}
              disabled={loading}
              className={`h-[var(--btn-h)] px-5 inline-flex items-center justify-center rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-error hover:bg-red-600 text-white ${loading ? "opacity-75 cursor-not-allowed" : ""}`}
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    ) : null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${animated ? "bg-black/40 opacity-100" : "bg-black/40 opacity-0"}`}
        onClick={handleClose}
      />

      {/* Desktop: slide from right */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] z-[61] flex-col bg-surface-card rounded-[13px] shadow-lg overflow-hidden transition-transform duration-300 ease-out ${
          animated ? "translate-x-0" : "translate-x-[calc(100%+32px)]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-black/90">{title}</h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 relative overflow-hidden">
          <div
            className={`absolute inset-0 overflow-y-auto px-5 py-4 transition-all duration-300 ease-out ${
              loading
                ? "opacity-0 -translate-y-3 pointer-events-none"
                : "opacity-100 translate-y-0"
            }`}
          >
            {formContent}
          </div>
          {savingState}
        </div>
        <div className="px-5 py-3 flex items-center justify-between shrink-0">
          {deleteButton}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={anySaving}
              className={`text-[15px] text-[#3C3C43]/60 hover:text-black/80 px-3 py-2 transition-colors ${anySaving ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveWithValidation}
              disabled={anySaving || isSaveDisabled}
              className={`inline-flex items-center gap-2 text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${isSaveDisabled ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
            >
              {bulk.saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saveButtonLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: bottom sheet */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[61] bg-[#F2F2F7] rounded-t-[13px] shadow-lg flex flex-col"
        style={{
          // Restamos `env(safe-area-inset-top)` para que el tope del
          // sheet quede por debajo de la status bar translúcida (PWA
          // standalone con viewport-fit=cover).
          height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          transform:
            dragY > 0
              ? `translateY(${dragY}px)`
              : animated
                ? "translateY(0)"
                : "translateY(100%)",
          transition: dragY === 0 ? "transform 0.3s ease-out" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div
          className="py-2 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={anySaving}
            className={`text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left ${anySaving ? "opacity-50" : ""}`}
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-black/90 truncate px-2">
            {title}
          </h3>
          <button
            type="button"
            onClick={handleSaveWithValidation}
            disabled={anySaving || isSaveDisabled}
            className={`text-[15px] font-semibold w-24 text-right active:opacity-50 transition-opacity ${isSaveDisabled ? "text-black/20 cursor-not-allowed" : "text-[#1644EB]"}`}
          >
            {saveButtonLabelMobile}
          </button>
        </div>
        {/* Scrollable content */}
        <div className="flex-1 relative overflow-hidden">
          <div
            className={`absolute inset-0 overflow-y-auto px-4 py-4 transition-all duration-300 ease-out ${
              loading
                ? "opacity-0 -translate-y-3 pointer-events-none"
                : "opacity-100 translate-y-0"
            }`}
          >
            {formContent}
          </div>
          {savingState}
        </div>
        {/* Footer - only shown when delete is available */}
        {editingVariant && onDeleteVariant && (
          <div className="px-4 py-3 flex items-center justify-center shrink-0">
            {deleteButton}
          </div>
        )}
      </div>

      {deleteConfirmModal}

      {/* Hidden input used by "Subir varios productos" / "Agregar más".
          Note: `accept` uses only MIME types (no extension fallback) — iOS
          Safari and some Android pickers fall back to a single-selection
          mode when the accept list mixes MIME types with extensions like
          `.heic`, so the `multiple` attribute gets effectively ignored.
          HEIC is covered by the explicit `image/heic` / `image/heif` MIME
          types and our app converts these client-side regardless. */}
      {!editingVariant && (
        <input
          ref={bulk.fileInputRef}
          type="file"
          accept="image/*,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={bulk.handleSelectFiles}
        />
      )}
    </>,
    document.body,
  );
};

export default VariantForm;
